import { useState, useEffect, useCallback } from 'react';
import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';
import type {
  TwoFactorStatus,
  TwoFactorSetupResponse,
  TwoFactorSetupRequest,
  TwoFactorVerificationRequest,
  BackupCodesResponse,
  ApiResponse,
} from '@/types/twoFactor';

interface UseTwoFactorReturn {
  // Status
  status: TwoFactorStatus | null;
  isLoading: boolean;
  error: string | null;
  
  // Setup flow
  setupData: TwoFactorSetupResponse | null;
  isSetupLoading: boolean;
  isCompleting: boolean;
  backupCodes: string[] | null;
  
  // Actions
  fetchStatus: () => Promise<void>;
  initiateSetup: () => Promise<TwoFactorSetupResponse>;
  completeSetup: (code: string) => Promise<boolean>;
  verifyCode: (code: string, useBackupCode?: boolean) => Promise<boolean>;
  disableTwoFactor: () => Promise<boolean>;
  regenerateBackupCodes: () => Promise<string[]>;
  
  // State management
  isDisabling: boolean;
  isRegenerating: boolean;
  clearError: () => void;
}

export const useTwoFactor = (): UseTwoFactorReturn => {
  // Status state
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Setup state
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [isSetupLoading, setIsSetupLoading] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  
  // Action states
  const [isDisabling, setIsDisabling] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Fetch current 2FA status
  const fetchStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await ApiService.request<{ data: TwoFactorStatus }>(
        EndpointKeys.GET_TWO_FACTOR_STATUS
      );
      
      setStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
      setError('Failed to load 2FA status');
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initiate 2FA setup
  const initiateSetup = useCallback(async (): Promise<TwoFactorSetupResponse> => {
    try {
      setIsSetupLoading(true);
      setError(null);
      
      const response = await ApiService.request<{ data: TwoFactorSetupResponse }>(
        EndpointKeys.INITIATE_TWO_FACTOR_SETUP,
        {
          method: 'POST',
          body: {},
        }
      );
      
      setSetupData(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to initiate 2FA setup:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to initiate 2FA setup';
      setError(errorMessage);
      throw err;
    } finally {
      setIsSetupLoading(false);
    }
  }, []);

  // Complete 2FA setup
  const completeSetup = useCallback(async (code: string): Promise<boolean> => {
    try {
      setIsCompleting(true);
      setError(null);
      
      const requestData: TwoFactorSetupRequest = { code };
      
      const response = await ApiService.request<{ 
        data: { 
          success: boolean; 
          backup_codes: string[]; 
          message: string; 
        } 
      }>(
        EndpointKeys.COMPLETE_TWO_FACTOR_SETUP,
        {
          method: 'POST',
          body: requestData,
        }
      );
      
      if (response.data.success) {
        setBackupCodes(response.data.backup_codes);
        setSetupData(null);
        // Refresh status
        await fetchStatus();
        return true;
      } else {
        setError('Failed to complete 2FA setup');
        return false;
      }
    } catch (err) {
      console.error('Failed to complete 2FA setup:', err);
      const errorMessage = err instanceof Error ? err.message : 'Invalid verification code';
      setError(errorMessage);
      return false;
    } finally {
      setIsCompleting(false);
    }
  }, [fetchStatus]);

  // Verify 2FA code
  const verifyCode = useCallback(async (code: string, useBackupCode = false): Promise<boolean> => {
    try {
      setError(null);
      
      const requestData: TwoFactorVerificationRequest = {
        code,
        use_backup_code: useBackupCode,
      };
      
      const response = await ApiService.request<{ 
        data: { 
          success: boolean; 
          message: string; 
          requires_backup_code?: boolean;
          attempts_remaining?: number;
        } 
      }>(
        EndpointKeys.VERIFY_TWO_FACTOR_CODE,
        {
          method: 'POST',
          body: requestData,
        }
      );
      
      if (response.data.success) {
        return true;
      } else {
        setError(response.data.message || 'Verification failed');
        return false;
      }
    } catch (err) {
      console.error('Failed to verify 2FA code:', err);
      const errorMessage = err instanceof Error ? err.message : 'Verification failed';
      setError(errorMessage);
      return false;
    }
  }, []);

  // Disable 2FA
  const disableTwoFactor = useCallback(async (): Promise<boolean> => {
    try {
      setIsDisabling(true);
      setError(null);
      
      const response = await ApiService.request<{ 
        data: { 
          success: boolean; 
          message: string; 
        } 
      }>(
        EndpointKeys.DISABLE_TWO_FACTOR,
        {
          method: 'POST',
          body: {},
        }
      );
      
      if (response.data.success) {
        // Refresh status
        await fetchStatus();
        return true;
      } else {
        setError('Failed to disable 2FA');
        return false;
      }
    } catch (err) {
      console.error('Failed to disable 2FA:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to disable 2FA';
      setError(errorMessage);
      return false;
    } finally {
      setIsDisabling(false);
    }
  }, [fetchStatus]);

  // Regenerate backup codes
  const regenerateBackupCodes = useCallback(async (): Promise<string[]> => {
    try {
      setIsRegenerating(true);
      setError(null);
      
      const response = await ApiService.request<{ 
        data: { 
          backup_codes: string[]; 
          count: number;
          message: string; 
        } 
      }>(
        EndpointKeys.REGENERATE_BACKUP_CODES,
        {
          method: 'POST',
          body: {},
        }
      );
      
      setBackupCodes(response.data.backup_codes);
      // Refresh status to update backup codes count
      await fetchStatus();
      return response.data.backup_codes;
    } catch (err) {
      console.error('Failed to regenerate backup codes:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to regenerate backup codes';
      setError(errorMessage);
      throw err;
    } finally {
      setIsRegenerating(false);
    }
  }, [fetchStatus]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load status on mount
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    // Status
    status,
    isLoading,
    error,
    
    // Setup flow
    setupData,
    isSetupLoading,
    isCompleting,
    backupCodes,
    
    // Actions
    fetchStatus,
    initiateSetup,
    completeSetup,
    verifyCode,
    disableTwoFactor,
    regenerateBackupCodes,
    
    // State management
    isDisabling,
    isRegenerating,
    clearError,
  };
};